import vocabulary from '../../management/errors.json' with { type: 'json' };

/**
 * The error vocabulary of beyond-cdn-management/1, read from `management/errors.json`.
 *
 * Codes are stable identifiers: they are compared, logged and looked up in the catalogs, never translated.
 */
export class Errors {
	/**
	 * Codes a management request can answer
	 *
	 * @returns {string[]}
	 */
	static get codes() {
		return Object.keys(vocabulary.codes);
	}

	/**
	 * Codes a job, stage, module or release failure can carry
	 *
	 * @returns {string[]}
	 */
	static get failures() {
		return Object.keys(vocabulary.failures);
	}

	/**
	 * Whose a failure is: `input`, `limit` or `service` (`origins` of `management/errors.json`)
	 *
	 * @param {string} code
	 * @returns {'input' | 'limit' | 'service'} `service` for a code this version does not know
	 */
	static origin(code) {
		for (const origin of ['input', 'limit', 'service']) if (vocabulary.origins?.[origin]?.includes(code)) return origin;
		return 'service';
	}

	/**
	 * @param {string} code
	 * @returns {number} The HTTP status of the code, 500 for a code this version does not know
	 */
	static status(code) {
		return vocabulary.codes[code]?.status ?? 500;
	}

	/**
	 * @param {string} code
	 */
	static has(code) {
		return code in vocabulary.codes;
	}

	/**
	 * The response body of a failure
	 *
	 * @param {string} code
	 * @param {string} message English technical text, free of secrets
	 * @param {object} [details]
	 */
	static body(code, message, details) {
		return { error: details ? { code, message, details } : { code, message } };
	}

	/**
	 * Reads the envelope of an answer without trusting its shape
	 *
	 * @param {unknown} body
	 * @returns {{code: string, message: string, details?: object} | undefined}
	 */
	static read(body) {
		const error = body && typeof body === 'object' ? body.error : undefined;
		if (!error || typeof error.code !== 'string') return;
		return { code: error.code, message: String(error.message ?? ''), ...(error.details ? { details: error.details } : {}) };
	}
}
